import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Eye, Filter } from "lucide-react";
import dayjs from "dayjs";
import { Select } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISALS } from "../../constants/api";

import StatusPill from "../../components/appraisal/StatusPill";

// Status options mirror the backend lifecycle keys.
const STATUS_OPTIONS = [
    "pending_employee_submission",
    "in_progress",
    "submitted_to_rm",
    "sent_back_by_rm",
    "pending_hr_approval",
    "hr_approved",
    "published",
    "overdue",
    "closed",
].map((s) => ({ value: s, label: s.replace(/_/g, " ") }));

const MyAppraisalList = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();

    const [appraisals, setAppraisals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const filters = { scope: "mine" };
            if (statusFilter) filters.status = statusFilter;
            const res = await fetch(APPRAISALS, filters);
            setAppraisals(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load appraisals");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => { loadData(); }, [loadData]);

    const filtered = appraisals.filter((a) => {
        if (!search) return true;
        return (a.cycle_name || "").toLowerCase().includes(search.toLowerCase());
    });

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={ClipboardList}
                title="My Appraisal"
                subtitle="Appraisal cycles assigned to you"
                totalRecords={filtered.length}
            />

            <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
                <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by cycle name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-xs"
                    />
                </div>
                <Select
                    placeholder="Status"
                    allowClear
                    value={statusFilter || undefined}
                    onChange={(v) => setStatusFilter(v || "")}
                    options={STATUS_OPTIONS}
                    style={{ minWidth: 200 }}
                />
            </div>

            <div className="px-3 pb-6 overflow-auto">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">No appraisals assigned to you.</div>
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-muted-foreground">
                                    <th className="px-3 py-2 font-medium">Cycle</th>
                                    <th className="px-3 py-2 font-medium hidden md:table-cell">Reporting Manager</th>
                                    <th className="px-3 py-2 font-medium hidden sm:table-cell">Submitted</th>
                                    <th className="px-3 py-2 font-medium">Status</th>
                                    <th className="px-3 py-2 font-medium text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((a) => (
                                    <tr key={a.id} className="border-t border-border hover:bg-muted/20">
                                        <td className="px-3 py-2">
                                            <button
                                                className="font-medium hover:text-sidebar-primary text-left"
                                                onClick={() => navigate(`/appraisal/my-appraisal/${a.id}`)}
                                            >
                                                {a.cycle_name || "—"}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2 hidden md:table-cell">{a.reporting_manager?.name || "—"}</td>
                                        <td className="px-3 py-2 hidden sm:table-cell text-xs">
                                            {a.self_submitted_at ? dayjs(a.self_submitted_at).format("DD MMM YYYY") : "—"}
                                        </td>
                                        <td className="px-3 py-2"><StatusPill value={a.status} /></td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => navigate(`/appraisal/my-appraisal/${a.id}`)}
                                            >
                                                <Eye className="w-4 h-4 mr-1" /> Open
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default MyAppraisalList;
